import React from 'react';
import { Card, CardContent, CardHeader } from '../Card';
import { renderIcon } from '../Dashboard/iconUtils';
import {
  BarChart,
  AreaChart,
  PieChart,
  LineChart,
} from '@plerionhq/design-system/experimental';

import { useTranslation } from 'react-i18next';

export type ChartType = 'line' | 'bar' | 'area' | 'pie' | 'doughnut';
export type ChartLayout = 'combined' | 'split' | 'stacked';

export interface ChartProps {
  /**
   * The title displayed at the top of the chart card.
   * Optional - if not provided, no title will be shown.
   *
   * @example
   * title="Monthly Revenue Trend"
   */
  title?: string;

  /**
   * The icon displayed next to the title.
   * Optional - if not provided, no icon will be shown.
   *
   * @example
   * titleIcon="bar-chart-3"
   */
  titleIcon?: string;

  /**
   * The subtitle displayed below the title.
   * Optional - if not provided, no subtitle will be shown.
   *
   * @example
   * subtitle="Data from Q1 2024"
   */
  subtitle?: string;

  /**
   * The type of chart to render.
   * Determines the visual representation of the data.
   *
   * - `line`: Line chart for trends and time series
   * - `bar`: Bar chart for categorical comparisons
   * - `area`: Filled area chart for volume visualization
   * - `pie`: Pie chart for proportions
   * - `doughnut`: Doughnut chart (pie with center space)
   *
   * @example
   * type="line"
   */
  type: ChartType;

  /**
   * The data array to be visualized.
   * Each object in the array represents a data point.
   * Supports nested properties accessible via dot notation in the fields prop.
   *
   * @example
   * data={[
   *   { month: 'Jan', revenue: 1000, profit: 300 },
   *   { month: 'Feb', revenue: 1200, profit: 350 }
   * ]}
   *
   * @example
   * // Nested data
   * data={[
   *   { timestamp: '2024-01-01', metrics: { score: 100, count: 50 } }
   * ]}
   */
  data?: Record<string, unknown>[];

  /**
   * Field mapping configuration that tells the chart how to interpret the data.
   *
   * @property {string} category - The field path for category/label values (x-axis for line/bar, labels for pie)
   * @property {string | string[]} values - The field path(s) for value data (y-axis for line/bar, values for pie)
   *
   * Supports dot notation for nested properties (e.g., 'metrics.score', 'user.profile.name').
   *
   * @example
   * // Single series
   * fields={{ category: 'month', values: 'revenue' }}
   *
   * @example
   * // Multiple series
   * fields={{ category: 'month', values: ['revenue', 'profit', 'costs'] }}
   *
   * @example
   * // Nested data
   * fields={{
   *   category: 'timestamp',
   *   values: 'metrics.compliance_posture_score'
   * }}
   */
  fields?: {
    category: string; // Supports dot notation: 'timestamp', 'user.profile.name', 'metrics.score'
    values: string | string[]; // Supports dot notation: 'value', 'metrics.score', ['metrics.score1', 'metrics.score2']
  };

  /**
   * The height of the chart in pixels.
   * Defaults to 400px if not specified.
   *
   * @default 400
   * @example
   * height={300}
   */
  height?: number;

  /**
   * Whether to display the legend showing series names.
   * Defaults to true.
   *
   * @default true
   * @example
   * showLegend={false}
   */
  showLegend?: boolean;

  /**
   * Custom color palette for the chart series.
   * If not provided, a brand color palette will be used (orange shades + complementary colors).
   * Array length should match the number of series for best results.
   *
   * @example
   * colors={['#f97316', '#ea580c', '#c2410c']}
   *
   * @example
   * colors={['#1f2937', '#dc2626', '#059669']}
   */
  colors?: string[];

  /**
   * The layout style for multiple series charts.
   *
   * - `combined`: All series displayed on the same chart (default)
   * - `split`: Each series gets its own sub-chart
   * - `stacked`: Series stacked on top of each other (bar and area charts only)
   *
   * Only affects charts with multiple series. Single series charts ignore this prop.
   *
   * @default 'combined'
   * @example
   * layout="split"
   */
  layout?: ChartLayout;

  /**
   * Custom field names for the legend.
   * Maps field paths to display names shown in the legend.
   * Useful for making technical field names more user-friendly.
   *
   * @example
   * fieldNames={{
   *   'metrics.compliance_posture_score': 'Compliance Score',
   *   'metrics.security_score': 'Security Rating'
   * }}
   *
   * @example
   * fieldNames={{
   *   'user.profile.metrics.score': 'User Score',
   *   'user.profile.metrics.performance': 'Performance'
   * }}
   */
  fieldNames?: {
    [key: string]: string; // 'metrics.compliance_score' => 'Compliance Score'
  };

  /** Whether data is streaming from LLM */
  isStreaming?: boolean;
}

// Utility function to extract nested values using dot notation
const extractNestedValue = (
  obj: Record<string, unknown>,
  path: string,
): unknown => {
  return path?.split('.').reduce((current: unknown, key: string): unknown => {
    if (
      current &&
      typeof current === 'object' &&
      current !== null &&
      key in current
    ) {
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
};

// Get custom field name or fallback to the path
const getFieldName = (
  path: string,
  fieldNames?: { [key: string]: string },
): string => {
  return fieldNames?.[path] || path?.split('.').pop() || path;
};

// Format number to be human-readable
const formatNumber = (num: number): string => {
  if (num === 0) {
    return '0';
  }

  const absNum = Math.abs(num);

  if (absNum >= 1e12) {
    return (num / 1e12).toFixed(1) + 'T';
  } else if (absNum >= 1e9) {
    return (num / 1e9).toFixed(1) + 'B';
  } else if (absNum >= 1e6) {
    return (num / 1e6).toFixed(1) + 'M';
  } else if (absNum >= 1e3) {
    return (num / 1e3).toFixed(1) + 'K';
  } else if (absNum >= 100) {
    return Math.round(num).toString();
  } else if (absNum >= 10) {
    return num.toFixed(1);
  } else {
    return num.toFixed(2);
  }
};

export const Chart: React.FC<ChartProps> = ({
  title,
  titleIcon,
  subtitle,
  type = 'line',
  data = [],
  fields,
  height = 400,
  showLegend = true,
  colors = [
    '#f97316', // Orange 500 - Primary brand color
    '#ea580c', // Orange 600 - Darker shade
    '#c2410c', // Orange 700 - Even darker
    '#fb923c', // Orange 400 - Lighter shade
    '#fdba74', // Orange 300 - Very light
    '#fed7aa', // Orange 200 - Lightest
    '#3b82f6', // Blue 500 - Complementary
    '#10b981', // Green 500 - Complementary
    '#8b5cf6', // Violet 500 - Complementary
    '#ef4444', // Red 500 - Complementary
  ],
  layout = 'combined',
  fieldNames,
}) => {
  const { t } = useTranslation('pleri/dashboard');
  // Show loading state if no data is provided
  if (!title && !subtitle && (!data || data.length === 0) && !fields) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex h-64 items-center justify-center">
            <div className="w-full animate-pulse">
              <div className="mb-4 h-4 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="h-48 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Default fields if not provided
  const chartFields = fields || { category: 'label', values: 'value' };
  // Extract category values
  const categoryValues = data.map((item) =>
    extractNestedValue(item, chartFields.category),
  );

  // Handle single vs multiple values
  const valueFields = Array.isArray(chartFields.values)
    ? chartFields.values
    : [chartFields.values];

  // Process data for charting
  const processedData = data.map((item, index) => {
    const dataPoint: Record<string, unknown> = {
      category: categoryValues[index],
    };

    valueFields.forEach((field, fieldIndex) => {
      const value = extractNestedValue(item, field);
      const fieldName = getFieldName(field, fieldNames);
      dataPoint[fieldName] = value;
      if (colors?.[fieldIndex]) {
        dataPoint[`${fieldName}_color`] = colors[fieldIndex];
      }
    });

    return dataPoint;
  });

  // Get series names for legend
  const seriesNames = valueFields.map((field) =>
    getFieldName(field, fieldNames),
  );

  // Render different chart types
  const renderChart = () => {
    const commonProps = {
      data: processedData as Record<string, string | number>[],
      series: seriesNames,
      categoryKey: 'category',
      showLegend,
      className: 'h-full w-full',
      deltaConfig: {
        isNeutralStyling: true,
        showDelta: false,
      },
      colors,
    };

    switch (type) {
      case 'line':
      case 'area': {
        const ChartComponent = type === 'line' ? LineChart : AreaChart;
        return (
          <ChartComponent
            {...commonProps}
            seriesProps={{
              isAnimationActive: false,
            }}
          />
        );
      }
      case 'bar':
        return (
          <BarChart
            {...commonProps}
            seriesProps={{ isAnimationActive: false }}
          />
        );

      case 'pie':
      case 'doughnut':
        return (
          <PieChart
            {...commonProps}
            legendProps={{
              align: 'center' as const,
              verticalAlign: 'bottom' as const,
            }}
            chartProps={{}}
            seriesProps={{
              innerRadius: type === 'doughnut' ? '40%' : '0%',
              outerRadius: '80%',
              labelLine: false,
              isAnimationActive: false,
              label: ({
                cx,
                cy,
                midAngle,
                innerRadius,
                outerRadius,
                index,
                value,
              }) => {
                const RADIAN = Math.PI / 180;
                const radius = innerRadius + (outerRadius - innerRadius) / 2;
                const x = cx + radius * Math.cos((midAngle || 0) * RADIAN);
                const y = cy + radius * Math.sin((midAngle || 0) * RADIAN);

                // Calculate total to determine percentage
                const total = processedData.reduce((sum, item) => {
                  const itemValue = Number(item[seriesNames[0]]);
                  return sum + (isNaN(itemValue) ? 0 : itemValue);
                }, 0);

                // Calculate percentage
                const percentage = total > 0 ? (value / total) * 100 : 0;

                // Don't render text if percentage is too small (less than 3%)
                const MIN_PERCENTAGE_THRESHOLD = 3;
                const shouldShowLabel = percentage >= MIN_PERCENTAGE_THRESHOLD;

                return (
                  <>
                    {shouldShowLabel && (
                      <text
                        x={x}
                        y={y}
                        fill="white"
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={12}
                        fontWeight="bold"
                      >
                        {`${value}`}
                      </text>
                    )}
                    {type === 'doughnut' && index === 0 && (
                      <g aria-hidden>
                        <text
                          x={cx}
                          y={cy}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fontSize={18}
                          fontWeight={700}
                          fill={document.documentElement.classList.contains('dark') ? '#f9fafb' : '#111827'}
                        >
                          {formatNumber(total)}
                        </text>
                      </g>
                    )}
                  </>
                );
              },
            }}
          />
        );

      default:
        return <div>{t('unsupportedChartType', { type })}</div>;
    }
  };

  // Render stacked layout for bar and area charts
  const renderStackedChart = () => {
    if (valueFields.length <= 1) {
      return renderChart();
    }

    // Only bar and area charts support stacking
    if (type !== 'bar' && type !== 'area') {
      console.warn(
        `Stacked layout is only supported for bar and area charts. Using combined layout for ${type} chart.`,
      );
      return renderChart();
    }

    const commonProps = {
      data: processedData as Record<string, string | number>[],
      series: seriesNames,
      categoryKey: 'category',
      showLegend,
      className: 'h-full w-full',
      deltaConfig: {
        isNeutralStyling: true,
        showDelta: false,
      },
      colors,
      seriesProps: {
        stackId: 'stack', // This enables stacking
      },
    };

    switch (type) {
      case 'bar':
        return <BarChart {...commonProps} />;
      case 'area':
        return <AreaChart {...commonProps} />;
      default:
        return renderChart();
    }
  };

  // Render split layout for multiple series
  const renderSplitCharts = () => {
    if (valueFields.length <= 1) {
      return renderChart();
    }

    // Get minmax grid template for responsive sizing
    const getGridTemplate = () => {
      const count = valueFields.length;

      if (count === 2) {
        return 'repeat(auto-fit, minmax(300px, 1fr))';
      } else if (count === 3) {
        return 'repeat(auto-fit, minmax(250px, 1fr))';
      } else if (count === 4) {
        return 'repeat(auto-fit, minmax(200px, 1fr))';
      } else {
        return 'repeat(auto-fit, minmax(180px, 1fr))';
      }
    };

    const gridTemplate = getGridTemplate();

    return (
      <div
        className="grid gap-4 pb-1"
        style={{ gridTemplateColumns: gridTemplate }}
      >
        {valueFields.map((field) => {
          const fieldName = getFieldName(field, fieldNames);
          const singleSeriesData = data.map((item, dataIndex) => ({
            category: categoryValues[dataIndex],
            [fieldName]: extractNestedValue(item, field),
          }));

          return (
            <div key={field} className="flex flex-col">
              <h4 className="mb-2 text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                {fieldName}
              </h4>
              <Chart
                title=""
                type={type}
                data={singleSeriesData as Record<string, unknown>[]}
                fields={{ category: 'category', values: fieldName }}
                height={300}
                showLegend={type === 'pie' || type === 'doughnut'}
                colors={colors}
                layout="combined"
              />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Card className="w-full">
      {(title || subtitle) && (
        <CardHeader>
          {title && (
            <h3 className="flex items-center gap-2 text-lg font-semibold">
              {titleIcon && (
                <span className="text-gray-500 dark:text-gray-400">
                  {renderIcon({ name: titleIcon, size: 20 })}
                </span>
              )}
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {subtitle}
            </p>
          )}
        </CardHeader>
      )}
      <CardContent className="">
        {layout === 'split' ? (
          renderSplitCharts()
        ) : layout === 'stacked' ? (
          <div style={{ height }}>{renderStackedChart()}</div>
        ) : (
          <div style={{ height }}>{renderChart()}</div>
        )}
      </CardContent>
    </Card>
  );
};
